import PropTypes from 'prop-types'
import styles from './AdventureReviews.module.scss'
import { defineMessage, FormattedMessage, useIntl } from 'react-intl'
import AdventureReviewRating from './AdventureReviewRating'
import AdventureSingleReview from './AdventureSingleReview'
import Loader from '../../helpers/Loader'
import useEmblaCarousel from 'embla-carousel-react'
import { useState, useRef, useCallback, useEffect, useMemo } from 'react'

const PREFETCH_THRESHOLD = 3
const MOBILE_BREAKPOINT = '(max-width: 768px)'

function useMediaQuery(query) {
  const getMatch = () => typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia(query).matches

  const [matches, setMatches] = useState(getMatch)

  useEffect(() => {
    if(typeof window === 'undefined' || typeof window.matchMedia !== 'function') { return }
    const mql = window.matchMedia(query)
    const onChange = (event) => setMatches(event.matches)
    setMatches(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [query])

  return matches
}

const reviewShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  rating: PropTypes.number.isRequired,
  publishDatetime: PropTypes.string,
  text: PropTypes.string,
})

const carouselPropTypes = {
  pages: PropTypes.arrayOf(PropTypes.arrayOf(reviewShape)).isRequired,
  hasMore: PropTypes.bool.isRequired,
  loadMore: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  reviewsUrl: PropTypes.string,
}

function DesktopCarousel({ pages, hasMore, loadMore, loading, reviewsUrl }) {
  const { formatMessage } = useIntl()
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    slidesToScroll: 1,
  })

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)
  const isProgrammaticScrollRef = useRef(false)
  const prevPagesLengthRef = useRef(1)

  const handleReachEnd = useCallback(() => {
    if(hasMore && !loading) { loadMore() }
  }, [hasMore, loading, loadMore])

  const scrollPrev = useCallback(() => {
    if(emblaApi) { emblaApi.scrollPrev() }
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if(!emblaApi) { return }
    if(emblaApi.canScrollNext()) {
      emblaApi.scrollNext()
    } else {
      handleReachEnd()
    }
  }, [emblaApi, handleReachEnd])

  const scrollNextSilent = useCallback(() => {
    if(!emblaApi) { return }
    isProgrammaticScrollRef.current = true
    emblaApi.scrollNext()
  }, [emblaApi])

  const onStateChange = useCallback(() => {
    if(!emblaApi) { return }
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext() && !hasMore)
  }, [emblaApi, hasMore])

  const onSelect = useCallback(() => {
    if(!emblaApi) { return }
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext() && !hasMore)
    if(!isProgrammaticScrollRef.current && !emblaApi.canScrollNext()) {
      handleReachEnd()
    }
    isProgrammaticScrollRef.current = false
  }, [emblaApi, handleReachEnd, hasMore])

  useEffect(() => {
    if(!emblaApi) { return }
    onStateChange()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onStateChange)
    return () => {
      emblaApi.off('select', onSelect)
      emblaApi.off('reInit', onStateChange)
    }
  }, [emblaApi, onSelect, onStateChange])

  useEffect(() => {
    if(!emblaApi || !pages.length) { return }
    const currentLength = pages.length
    emblaApi.reInit()
    if(currentLength > prevPagesLengthRef.current) {
      window.requestAnimationFrame(() => scrollNextSilent())
    }
    prevPagesLengthRef.current = currentLength
  }, [emblaApi, pages.length, scrollNextSilent])

  return (
    <div className={styles.carouselWrapper}>
      {!prevBtnDisabled && (
        <button
          className={`${styles.arrowBtn} ${styles.arrowPrev}`}
          onClick={scrollPrev}
          aria-label={formatMessage(defineMessage({ defaultMessage: 'Previous reviews' }))}
        >
          <i className='font-icons left-arrow-icon' />
        </button>
      )}

      {!nextBtnDisabled && (
        <button
          className={`${styles.arrowBtn} ${styles.arrowNext}`}
          onClick={scrollNext}
          aria-label={formatMessage(defineMessage({ defaultMessage: 'Next reviews' }))}
        >
          <i className='font-icons right-arrow-icon' />
        </button>
      )}

      <div className={styles.emblaViewport} ref={emblaRef}>
        <div className={styles.emblaContainer}>
          {pages.map((page, i) => (
            <div className={styles.slide} key={`page-${i}`}>
              <div className={styles.slideGrid}>
                {page.map((review) => (
                  <AdventureSingleReview key={review.id} review={review} />
                ))}
                {!hasMore && i === pages.length - 1 && reviewsUrl && (
                  <a
                    href={reviewsUrl}
                    target='_blank'
                    rel='noreferrer noopener'
                    className={styles.viewMoreLink}
                  >
                    <FormattedMessage defaultMessage='View More' />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {loading && (
        <div className={styles.loaderOverlay}>
          <Loader isLoading={loading} />
        </div>
      )}
    </div>
  )
}

DesktopCarousel.propTypes = carouselPropTypes

function MobileCarousel({ pages, hasMore, loadMore, loading, reviewsUrl }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    slidesToScroll: 1,
  })

  const isProgrammaticScrollRef = useRef(false)
  const prevFlatLengthRef = useRef(0)

  const flatReviews = useMemo(
    () => pages.flatMap((page) => page),
    [pages],
  )

  const flatReviewsLengthRef = useRef(flatReviews.length)
  const hasMoreRef = useRef(hasMore)
  const loadingRef = useRef(loading)
  const loadMoreRef = useRef(loadMore)

  useEffect(() => {
    flatReviewsLengthRef.current = flatReviews.length
    hasMoreRef.current = hasMore
    loadingRef.current = loading
    loadMoreRef.current = loadMore
  }, [flatReviews.length, hasMore, loading, loadMore])

  const scrollNextSilent = useCallback(() => {
    if(!emblaApi) { return }
    isProgrammaticScrollRef.current = true
    emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if(!emblaApi) { return }
    if(isProgrammaticScrollRef.current) {
      isProgrammaticScrollRef.current = false
      return
    }
    const currentIndex = emblaApi.selectedScrollSnap()
    if(currentIndex >= flatReviewsLengthRef.current - PREFETCH_THRESHOLD &&
      hasMoreRef.current &&
      !loadingRef.current) {
      loadMoreRef.current()
    }
  }, [emblaApi])

  useEffect(() => {
    if(!emblaApi) { return }
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  useEffect(() => {
    if(!emblaApi || !flatReviews.length) { return }
    const currentLength = flatReviews.length
    emblaApi.reInit()
    if(currentLength > prevFlatLengthRef.current && prevFlatLengthRef.current > 0) {
      window.requestAnimationFrame(() => scrollNextSilent())
    }
    prevFlatLengthRef.current = currentLength
  }, [emblaApi, flatReviews.length, scrollNextSilent])

  return (
    <div className={styles.mobileCarouselWrapper}>
      <div className={styles.mobileEmblaViewport} ref={emblaRef}>
        <div className={styles.emblaContainer}>
          {flatReviews.map((review, i) => (
            <div className={styles.mobileSlide} key={review.id}>
              <AdventureSingleReview review={review} />
              {!hasMore && i === flatReviews.length - 1 && reviewsUrl && (
                <a
                  href={reviewsUrl}
                  target='_blank'
                  rel='noreferrer noopener'
                  className={styles.viewMoreLink}
                >
                  <FormattedMessage defaultMessage='View More' />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {loading && (
        <div className={styles.loaderOverlay}>
          <Loader isLoading={loading} />
        </div>
      )}
    </div>
  )
}

MobileCarousel.propTypes = carouselPropTypes

export default function AdventureReviews({
  pages,
  count,
  rating,
  hasMore,
  loadMore,
  loading,
  reviewsUrl,
}) {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT)

  return (
    <div className={`${styles.rightSide} short-description key-info`}>
      <div className={styles.reviewContainer}>
        <AdventureReviewRating rating={rating} />
        <FormattedMessage
          defaultMessage='{reviews, plural, =0 {No reviews} one {# Review} other {# Reviews}}'
          values={{ reviews: count }}
        />
      </div>
      {isMobile
        ? (
          <MobileCarousel
            pages={pages}
            hasMore={hasMore}
            loadMore={loadMore}
            loading={loading}
            reviewsUrl={reviewsUrl}
          />
        )
        : (
          <DesktopCarousel
            pages={pages}
            hasMore={hasMore}
            loadMore={loadMore}
            loading={loading}
            reviewsUrl={reviewsUrl}
          />
        )}
    </div>
  )
}

AdventureReviews.propTypes = {
  pages: PropTypes.arrayOf(PropTypes.arrayOf(reviewShape)).isRequired,
  count: PropTypes.number.isRequired,
  rating: PropTypes.number.isRequired,
  hasMore: PropTypes.bool.isRequired,
  loadMore: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  reviewsUrl: PropTypes.string,
}
